import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Calendar, ChevronRight, Mic2, MessageSquarePlus, Send, Music, Check, Loader2, Users, Mail, Play, Pause, Upload, Clock } from 'lucide-react';
import axios from 'axios';
import Marquee from 'react-fast-marquee';
import { toast } from 'sonner';
import { usePlayer } from '@/contexts/PlayerContext';
import { track } from '@/lib/analytics';
import AdSlot from '@/components/AdSlot';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Get current date/time in WAT (UTC+1)
const getWATDate = () => {
  const now = new Date();
  return new Date(now.getTime() + (1 * 60 * 60 * 1000) + (now.getTimezoneOffset() * 60 * 1000));
};

export default function LandingPage() {
  const { startRadio, isPlaying, togglePlay, radioMode } = usePlayer();
  const [nowPlaying, setNowPlaying] = useState(null);
  const [shows, setShows] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [requestForm, setRequestForm] = useState({ song_name: '', artist_name: '', listener_name: '', listener_email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [activeListeners, setActiveListeners] = useState(0);
  const [recentTracks, setRecentTracks] = useState([]);
  const [radioNow, setRadioNow] = useState(null);
  const [schedule, setSchedule] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [npRes, showsRes, schedRes, hostsRes, reqRes, radioRes, recentRes] = await Promise.all([
          axios.get(`${API}/schedule/now`),
          axios.get(`${API}/shows`),
          axios.get(`${API}/schedule`),
          axios.get(`${API}/hosts`),
          axios.get(`${API}/requests/recent`),
          axios.get(`${API}/radio/nowplaying`),
          axios.get(`${API}/radio/recent`)
        ]);
        setNowPlaying(npRes.data.now_playing);
        setShows(showsRes.data.shows || []);
        setHosts(hostsRes.data.hosts || []);
        setRecentRequests(reqRes.data.requests || []);
        setActiveListeners(radioRes.data.listeners || 0);
        setRadioNow(radioRes.data);
        setRecentTracks(recentRes.data.recent || []);
        const watNow = getWATDate();
        const today = watNow.getDay();
        const dayIndex = today === 0 ? 6 : today - 1;
        setSchedule(schedRes.data.schedule || []);
        const todayEntries = (schedRes.data.schedule || []).filter(e => e.day_of_week === dayIndex);
        todayEntries.sort((a, b) => a.start_time.localeCompare(b.start_time));
        setTodaySchedule(todayEntries);
      } catch (e) {
        console.error('Failed to fetch data', e);
      }
    };
    fetchData();
  }, []);

  // Poll RadioBOSS listener count + recent tracks
  useEffect(() => {
    const poll = () => {
      axios.get(`${API}/radio/nowplaying`).then(r => { setActiveListeners(r.data.listeners || 0); setRadioNow(r.data); }).catch(() => {});
      axios.get(`${API}/radio/recent`).then(r => setRecentTracks(r.data.recent || [])).catch(() => {});
    };
    const interval = setInterval(poll, 15000);
    return () => clearInterval(interval);
  }, []);

  const getHostImageSrc = (host) => {
    if (host.image_data && host.image_data.startsWith('data:')) return host.image_data;
    if (host.image_storage_path) return `${API}/host-image/${host.id}`;
    if (host.image_url && !host.image_url.startsWith('__storage__')) return host.image_url;
    return 'https://images.unsplash.com/photo-1581092800573-6afa755dcdc7?w=400';
  };

  const getShowImageSrc = (show) => {
    if (show.image_data && show.image_data.startsWith('data:')) return show.image_data;
    if (show.image_storage_path) return `${API}/show-image/${show.id}`;
    if (show.image_url && !show.image_url.startsWith('__storage__') && show.image_url.startsWith('http')) return show.image_url;
    return null;
  };

  const upcomingAll = React.useMemo(() => {
    if (!schedule.length) return [];
    const wat = getWATDate();
    const todayIdx = wat.getDay() === 0 ? 6 : wat.getDay() - 1;
    const nowMins = wat.getHours() * 60 + wat.getMinutes();
    const parseHM = (t) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
    const relLabel = (m) => {
      if (m < 60) return `in ${m}m`;
      const h = Math.floor(m / 60), mm = m % 60;
      if (h < 24) return mm ? `in ${h}h ${mm}m` : `in ${h}h`;
      const d = Math.floor(h / 24);
      return `in ${d}d`;
    };
    return schedule
      .map(e => {
        const dayOffset = (e.day_of_week - todayIdx + 7) % 7;
        const start = parseHM(e.start_time);
        let minutesFromNow = dayOffset * 24 * 60 + (start - nowMins);
        if (minutesFromNow < 0) minutesFromNow += 7 * 24 * 60;
        return { entry: e, minutesFromNow, label: relLabel(minutesFromNow) };
      })
      .filter(x => x.minutesFromNow > 0 && x.minutesFromNow < 24 * 60 * 2)
      .sort((a, b) => a.minutesFromNow - b.minutesFromNow)
      .slice(0, 6);
  }, [schedule]);

  const handleRequestSubmit = async (e) => {    e.preventDefault();
    if (!requestForm.song_name.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/requests`, requestForm);
      track('song_request_submit', { has_email: Boolean(requestForm.listener_email) });
      setSubmitted(true);
      setRequestForm({ song_name: '', artist_name: '', listener_name: '', listener_email: '', message: '' });
      toast.success('Song request submitted!');
      setTimeout(() => setSubmitted(false), 4000);
    } catch {
      toast.error('Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="landing-page" className="pb-28">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1579975194233-8a4c97b11f1b?w=1600&q=80"
            alt="Gospel concert"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0F1115]/70 via-[#0F1115]/80 to-[#0F1115]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 animate-fade-in-up">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-3 h-3 bg-red-500 rounded-full live-pulse" />
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37]">Live 24/7</span>
              {activeListeners > 0 && (
                <span data-testid="listener-count" className="flex items-center gap-1.5 text-xs text-[#94A3B8] bg-white/5 px-3 py-1 rounded-full">
                  <Users size={12} className="text-emerald-400" />
                  <span className="text-emerald-400 font-semibold">{activeListeners}</span> listening now
                </span>
              )}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-white mb-6" style={{ fontFamily: 'Clash Display, sans-serif' }}>
              Christovibes
            </h1>
            <p className="text-base sm:text-lg text-[#94A3B8] mb-8 max-w-xl leading-relaxed">
              Your 24-hour radio for contemporary African Christian music. Gospel, worship, praise, and more — broadcasting live, always.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                data-testid="listen-live-btn"
                onClick={() => {
                  if (radioMode && isPlaying) togglePlay();
                  else { startRadio(); track('listen_live', { source: 'landing_hero' }); }
                }}
                className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-8 py-3.5 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95"
              >
                {radioMode && isPlaying ? <><Pause size={20} /> Pause Radio</> : <><Play size={20} className="ml-0.5" /> Listen Live</>}
              </button>
              <Link
                to="/schedule"
                data-testid="view-schedule-btn"
                className="bg-white/5 text-white font-medium hover:bg-white/10 border border-white/10 transition-colors rounded-full px-8 py-3.5 flex items-center gap-2"
              >
                <Calendar size={20} /> View Schedule
              </Link>
              <Link
                to="/shows"
                data-testid="view-shows-btn"
                className="bg-white/5 text-white font-medium hover:bg-white/10 border border-white/10 transition-colors rounded-full px-8 py-3.5 flex items-center gap-2 hidden sm:flex"
              >
                <Mic2 size={20} /> Our Shows
              </Link>
              <Link
                to="/submit-music"
                data-testid="submit-music-cta"
                className="text-[#D4AF37] font-medium border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 transition-colors rounded-full px-8 py-3.5 flex items-center gap-2"
              >
                <Upload size={18} /> Submit your music
              </Link>
            </div>
            </div>

            {/* Right column — Now Playing artwork + Up Next schedule (desktop/tablet) */}
            <div data-testid="hero-side-panel" className="hidden lg:flex lg:col-span-5 flex-col gap-5 animate-fade-in-up">
              <div className="bg-[#171A21]/80 backdrop-blur-md rounded-3xl border border-white/10 p-5 shadow-2xl">
                <div className="relative aspect-square rounded-2xl overflow-hidden bg-gradient-to-br from-[#D4AF37]/20 to-[#991B1B]/20">
                  {radioNow?.cover_url ? (
                    <img
                      src={`${radioNow.cover_url}?${Math.floor(Date.now() / 30000)}`}
                      alt="Now playing"
                      className="w-full h-full object-cover"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music size={72} className="text-[#D4AF37]/50" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/60 backdrop-blur px-2.5 py-1 rounded-full">
                    <div className="w-1.5 h-1.5 bg-red-500 rounded-full live-pulse" />
                    <span className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-[0.15em]">Now Playing</span>
                  </div>
                </div>
                {radioNow?.title && (
                  <div className="mt-4">
                    <p className="text-white text-lg font-semibold leading-tight truncate">{radioNow.title}</p>
                    <p className="text-[#D4AF37] text-sm mt-1 truncate">{radioNow.artist}</p>
                    {radioNow.next_title && (
                      <p className="text-[#94A3B8] text-xs mt-3 truncate">
                        Up next: <span className="text-white/70">{radioNow.next_artist} — {radioNow.next_title}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div data-testid="hero-upcoming-panel" className="bg-[#171A21]/80 backdrop-blur-md rounded-3xl border border-white/10 p-5 shadow-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={14} className="text-[#D4AF37]" />
                  <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#D4AF37]">Up Next</span>
                  <Link to="/schedule" className="ml-auto text-[11px] text-[#D4AF37] hover:text-[#E8C24D] flex items-center gap-0.5">
                    Full <ChevronRight size={12} />
                  </Link>
                </div>
                {upcomingAll.length === 0 ? (
                  <p className="text-[#94A3B8] text-sm text-center py-4">Schedule coming soon.</p>
                ) : (
                  <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
                    {upcomingAll.map((u, i) => (
                      <Link
                        key={u.entry.id}
                        to={u.entry.show?.id ? `/shows/${u.entry.show.id}` : '/schedule'}
                        data-testid={`hero-upcoming-${i}`}
                        className="flex items-center gap-3 bg-[#0A0C10]/60 border border-white/5 rounded-xl p-2.5 hover:bg-[#0A0C10] hover:border-[#D4AF37]/20 transition-colors"
                      >
                        <div className="w-11 h-11 rounded-lg overflow-hidden bg-[#1F232C] flex items-center justify-center shrink-0">
                          {u.entry.show && getShowImageSrc(u.entry.show) ? (
                            <img src={getShowImageSrc(u.entry.show)} alt={u.entry.show.name} className="w-full h-full object-cover" />
                          ) : (
                            <Radio size={18} className="text-[#D4AF37]/70" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-white text-sm font-semibold truncate">{u.entry.show?.name || 'TBA'}</p>
                          <p className="text-[#94A3B8] text-xs truncate">
                            {DAYS[u.entry.day_of_week].slice(0, 3)} · {u.entry.start_time} WAT
                          </p>
                        </div>
                        <span className="text-[11px] text-[#D4AF37] font-semibold shrink-0">{u.label}</span>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Homepage hero ad — appears only when enabled + consent + valid slot */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <AdSlot slotKey="home" page="/" className="mt-6 mb-2" />
      </div>

      {/* Now Playing Banner */}
      {nowPlaying && nowPlaying.show && (
        <section data-testid="now-playing-banner" className="bg-[#171A21] border-y border-white/5 py-4">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-2 h-2 bg-red-500 rounded-full live-pulse" />
              <span className="text-xs font-bold text-red-400 uppercase tracking-wider">On Air</span>
            </div>
            <Marquee gradient={false} speed={40} className="text-[#94A3B8]">
              <span className="mx-8">
                <span className="text-white font-medium">{nowPlaying.show.name}</span>
                {nowPlaying.host && <span className="ml-2 text-[#D4AF37]">with {nowPlaying.host.name}</span>}
                <span className="ml-2">| {nowPlaying.start_time} - {nowPlaying.end_time} WAT</span>
              </span>
            </Marquee>
          </div>
        </section>
      )}

      {/* Now On Air — Featured Block */}
      {nowPlaying && nowPlaying.show && (
        <section className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div data-testid="now-on-air-block" className="bg-[#171A21] rounded-2xl border border-white/5 overflow-hidden hover:border-[#D4AF37]/30 transition-colors">
              <Link to={`/shows/${nowPlaying.show.id}`} data-testid="now-on-air-link" className="flex flex-col md:flex-row group">
                <div className="md:w-72 h-48 md:h-auto bg-gradient-to-br from-[#D4AF37]/20 to-[#991B1B]/20 flex items-center justify-center relative shrink-0 overflow-hidden">
                  {getShowImageSrc(nowPlaying.show) ? (
                    <img src={getShowImageSrc(nowPlaying.show)} alt={nowPlaying.show.name} className="w-full h-full object-cover" />
                  ) : (
                    <Radio size={64} className="text-[#D4AF37]/40" />
                  )}
                  <div className="absolute top-4 left-4 flex items-center gap-2">
                    <div className="w-2.5 h-2.5 bg-red-500 rounded-full live-pulse" />
                    <span className="text-xs font-bold text-red-400 uppercase tracking-wider bg-black/40 backdrop-blur px-2 py-1 rounded-full">Live Now</span>
                  </div>
                </div>
                <div className="p-6 sm:p-8 flex-1">
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                    {nowPlaying.show.name}
                  </h2>
                  <p className="text-[#94A3B8] mt-3 max-w-lg">{nowPlaying.show.description}</p>
                  {nowPlaying.host && (
                    <div className="flex items-center gap-3 mt-5 pt-5 border-t border-white/5">
                      <img src={getHostImageSrc(nowPlaying.host)} alt={nowPlaying.host.name} className="w-10 h-10 rounded-full object-cover" />
                      <div>
                        <p className="text-white text-sm font-medium">{nowPlaying.host.name}</p>
                        <p className="text-[#94A3B8] text-xs">{nowPlaying.start_time} - {nowPlaying.end_time} WAT</p>
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Now Playing on Stream + Recent Tracks */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Now Playing Card */}
            {radioNow?.title && (
              <div data-testid="stream-now-playing" className="lg:col-span-2 bg-[#171A21] rounded-2xl border border-white/5 p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-2 h-2 bg-red-500 rounded-full live-pulse" />
                  <span className="text-xs font-bold text-red-400 uppercase tracking-wider">Now Playing</span>
                  {activeListeners > 0 && <span className="text-xs text-[#94A3B8] ml-auto">{activeListeners} listening</span>}
                </div>
                <div className="flex items-start gap-4 flex-1">
                  {radioNow.cover_url && (
                    <img
                      src={`${radioNow.cover_url}?${Math.floor(Date.now() / 10000)}`}
                      alt="Album art"
                      className="w-24 h-24 rounded-xl object-cover shrink-0 shadow-lg"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div className="min-w-0">
                    <p className="text-white text-xl font-semibold leading-tight">{radioNow.title}</p>
                    <p className="text-[#D4AF37] text-sm mt-1.5">{radioNow.artist}</p>
                    {radioNow.next_title && (
                      <p className="text-[#94A3B8] text-xs mt-4">
                        Up next: <span className="text-white/70">{radioNow.next_artist} — {radioNow.next_title}</span>
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Recent Tracks */}
            <div data-testid="recent-tracks-panel" className={`${radioNow?.title ? 'lg:col-span-3' : 'lg:col-span-5'} bg-[#171A21] rounded-2xl border border-white/5 p-6`}>
              <div className="flex items-center gap-2 mb-5">
                <Music size={16} className="text-[#D4AF37]" />
                <h3 className="text-white font-semibold">Recently Played</h3>
              </div>
              {recentTracks.length > 0 ? (
                <div className="space-y-1">
                  {recentTracks.map((track, i) => (
                    <div key={i} data-testid={`recent-track-${i}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.03] transition-colors">
                      <div className="w-9 h-9 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center shrink-0">
                        <Music size={14} className="text-[#D4AF37]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium truncate">{track.title}</p>
                        <p className="text-[#94A3B8] text-xs truncate">{track.artist}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[#94A3B8] text-sm text-center py-6">No recent tracks available</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Today's Schedule */}
      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-2">Today's Lineup</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                {DAYS[getWATDate().getDay() === 0 ? 6 : getWATDate().getDay() - 1]} Schedule
              </h2>
            </div>
            <Link to="/schedule" className="text-[#D4AF37] text-sm font-medium flex items-center gap-1 hover:text-[#E8C24D] transition-colors">
              Full Schedule <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {todaySchedule.map((entry, i) => {
              const now = new Date();
              const currentTime = `${String(now.getUTCHours()).padStart(2,'0')}:${String(now.getUTCMinutes()).padStart(2,'0')}`;
              const isLive = entry.start_time <= currentTime && entry.end_time > currentTime;
              return (
                <div
                  key={entry.id}
                  data-testid={`schedule-entry-${i}`}
                  className={`p-5 rounded-2xl border transition-all duration-300 ${
                    isLive
                      ? 'bg-[#171A21] border-l-4 border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.15)]'
                      : 'bg-[#171A21] border-white/5 hover:-translate-y-1'
                  }`}
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-mono text-[#94A3B8]">{entry.start_time} - {entry.end_time}</span>
                    {isLive && (
                      <span className="flex items-center gap-1.5 text-xs font-bold text-red-400">
                        <span className="w-2 h-2 bg-red-500 rounded-full live-pulse" /> LIVE
                      </span>
                    )}
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-1">{entry.show?.name || 'TBA'}</h3>
                  {entry.host && <p className="text-[#D4AF37] text-sm">{entry.host.name}</p>}
                </div>
              );
            })}
            {todaySchedule.length === 0 && (
              <p className="text-[#94A3B8] col-span-full text-center py-8">No shows scheduled for today yet.</p>
            )}
          </div>
        </div>
      </section>

      {/* Featured Shows */}
      <section className="py-20 sm:py-24 bg-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-10">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-2">Our Shows</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                Featured Programs
              </h2>
            </div>
            <Link to="/shows" className="text-[#D4AF37] text-sm font-medium flex items-center gap-1 hover:text-[#E8C24D] transition-colors">
              All Shows <ChevronRight size={16} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {shows.slice(0, 4).map((show, i) => (
              <Link
                to={`/shows/${show.id}`}
                key={show.id}
                data-testid={`show-card-${i}`}
                className="bg-[#171A21] rounded-2xl border border-white/5 shadow-2xl overflow-hidden hover:-translate-y-1 hover:border-[#D4AF37]/30 transition-all duration-300 group"
              >
                <div className="h-40 bg-gradient-to-br from-[#D4AF37]/20 to-[#991B1B]/20 flex items-center justify-center overflow-hidden">
                  {getShowImageSrc(show) ? (
                    <img src={getShowImageSrc(show)} alt={show.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <Radio size={48} className="text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors" />
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-white font-semibold text-lg mb-2">{show.name}</h3>
                  <p className="text-[#94A3B8] text-sm line-clamp-2">{show.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Our Hosts */}
      {hosts.length > 0 && (
        <section className="py-20 sm:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-2">On-Air Personalities</p>
                <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                  Meet Your Hosts
                </h2>
              </div>
              <Link to="/shows" className="text-[#D4AF37] text-sm font-medium flex items-center gap-1 hover:text-[#E8C24D] transition-colors">
                View All <ChevronRight size={16} />
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {hosts.slice(0, 4).map((host, i) => (
                <div key={host.id} data-testid={`host-preview-${i}`} className="text-center group">
                  <div className="w-28 h-28 mx-auto rounded-full overflow-hidden border-2 border-white/10 group-hover:border-[#D4AF37]/40 transition-colors mb-3">
                    <img src={getHostImageSrc(host)} alt={host.name} className="w-full h-full object-cover" />
                  </div>
                  <p className="text-white font-medium text-sm">{host.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Song Request Section */}
      <section className="py-20 sm:py-24 bg-[#0A0C10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Request Form */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2.5 rounded-xl bg-[#D4AF37]/10">
                  <MessageSquarePlus size={22} className="text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37]">Request a Song</p>
                  <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                    What do you want to hear?
                  </h2>
                </div>
              </div>
              <p className="text-[#94A3B8] mb-6">Send your song request to our DJs. We'll do our best to play it on air!</p>

              {submitted ? (
                <div data-testid="request-success" className="bg-[#171A21] rounded-2xl border border-emerald-500/20 p-8 text-center animate-fade-in-up">
                  <div className="w-14 h-14 mx-auto bg-emerald-500/10 rounded-full flex items-center justify-center mb-4">
                    <Check size={28} className="text-emerald-400" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">Request Submitted!</h3>
                  <p className="text-[#94A3B8] text-sm">Our DJ will review your request. Tune in to hear your song!</p>
                </div>
              ) : (
                <form data-testid="song-request-form" onSubmit={handleRequestSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Song Name *</label>
                      <input
                        data-testid="request-song-input"
                        type="text"
                        value={requestForm.song_name}
                        onChange={e => setRequestForm({...requestForm, song_name: e.target.value})}
                        placeholder="e.g. Way Maker"
                        className="bg-[#171A21] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 transition-all"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Artist</label>
                      <input
                        data-testid="request-artist-input"
                        type="text"
                        value={requestForm.artist_name}
                        onChange={e => setRequestForm({...requestForm, artist_name: e.target.value})}
                        placeholder="e.g. Sinach"
                        className="bg-[#171A21] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Your Name</label>
                    <input
                      data-testid="request-name-input"
                      type="text"
                      value={requestForm.listener_name}
                      onChange={e => setRequestForm({...requestForm, listener_name: e.target.value})}
                      placeholder="Your name (optional)"
                      className="bg-[#171A21] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">
                      <span className="flex items-center gap-1.5">
                        <Mail size={13} /> Email <span className="text-[#94A3B8]/50 font-normal">(get notified when your song is played)</span>
                      </span>
                    </label>
                    <input
                      data-testid="request-email-input"
                      type="email"
                      value={requestForm.listener_email}
                      onChange={e => setRequestForm({...requestForm, listener_email: e.target.value})}
                      placeholder="your@email.com (optional)"
                      className="bg-[#171A21] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#94A3B8] mb-1.5 block">Message to DJ</label>
                    <textarea
                      data-testid="request-message-input"
                      value={requestForm.message}
                      onChange={e => setRequestForm({...requestForm, message: e.target.value})}
                      placeholder="Shoutout or dedication (optional)"
                      rows={2}
                      maxLength={200}
                      className="bg-[#171A21] border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none w-full placeholder:text-slate-600 transition-all resize-none"
                    />
                  </div>
                  <button
                    data-testid="submit-request-btn"
                    type="submit"
                    disabled={submitting}
                    className="bg-[#D4AF37] text-[#0F1115] font-semibold hover:bg-[#E8C24D] transition-colors rounded-full px-8 py-3.5 shadow-lg shadow-[#D4AF37]/20 flex items-center gap-2 active:scale-95 disabled:opacity-50"
                  >
                    {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <><Send size={18} /> Send Request</>}
                  </button>
                </form>
              )}
            </div>

            {/* Recently Played Requests */}
            <div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#D4AF37] mb-3">Listener Picks</p>
              <h3 className="text-xl sm:text-2xl tracking-tight font-medium text-white mb-6" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                Recently Played Requests
              </h3>
              {recentRequests.length > 0 ? (
                <div className="space-y-3">
                  {recentRequests.map((req, i) => (
                    <div key={req.id} data-testid={`recent-request-${i}`} className="bg-[#171A21] rounded-xl border border-white/5 p-4 flex items-start gap-3 hover:bg-white/[0.03] transition-colors">
                      <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Music size={16} className="text-emerald-400" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-medium text-sm">{req.song_name}</p>
                        {req.artist_name && <p className="text-[#D4AF37] text-xs">{req.artist_name}</p>}
                        {req.message && <p className="text-[#94A3B8] text-xs mt-1 italic">"{req.message}"</p>}
                        <p className="text-[#94A3B8]/60 text-xs mt-1">Requested by {req.listener_name}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-[#171A21] rounded-2xl border border-white/5 p-8 text-center">
                  <Music size={32} className="mx-auto text-[#94A3B8]/30 mb-3" />
                  <p className="text-[#94A3B8] text-sm">No played requests yet. Be the first to request a song!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
